import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { X, User, BookOpen, Award, TrendingUp, Users, CheckCircle, Calendar, GraduationCap, AlertTriangle, Edit, FileText, Save, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { loadStudentPhoto, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';

interface StudentInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: {
    Unid: string;
    Name: string;
    Vorname: string;
    Klasse: string;
    Foto?: string;
    sophomorixUnid?: string;
  };
}

interface SubjectProgressData {
  totalTasks: number;
  completedTasks: number;
  progressPercentage: number;
  regularTasks?: number;
  completedRegularTasks?: number;
  baseProgress?: number;
  bonusTasks?: number;
  completedBonusTasks?: number;
  bonusProgress?: number;
  learningPlansCount?: number;
  learningPlans: Array<{
    LPID: number;
    NAME: string;
    START_DATUM: string;
    END_DATUM: string;
  }>;
  error?: string;
}

interface StudentProgressData {
  unid: string;
  overallProgress: {
    totalTasks: number;
    completedTasks: number;
    progressPercentage: number;
    regularTasks?: number;
    completedRegularTasks?: number;
    baseProgress?: number;
    bonusTasks?: number;
    completedBonusTasks?: number;
    bonusProgress?: number;
  };
  subjectProgress: {
    Deutsch: SubjectProgressData;
    Mathematik: SubjectProgressData;
    Englisch: SubjectProgressData;
  };
}

interface PerformanceRecord {
  Fach: string;
  Baustein: string;
  Lernplan: string;
  Aufgabe: string;
  Lehrer: string;
  Datum: string;
}

interface KIAssessment {
  id: number;
  unid: string;
  memo: string;
  datum: string;
  farbe: 'red' | 'green' | 'normal';
}

interface StudentData {
  ID: number;
  Name: string;
  Vorname: string;
  Klasse: string;
  email: string;
  RFID: string;
  sophomorixUnid: string;
  Diff_D: string;
  Diff_M: string;
  Diff_E: string;
  Memo?: string;
  Foto?: string;
}

const subjectColors = {
  Deutsch: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-900',
    progress: 'bg-blue-600',
    icon: 'text-blue-600'
  },
  Mathematik: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-900',
    progress: 'bg-green-600',
    icon: 'text-green-600'
  },
  Englisch: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-900',
    progress: 'bg-purple-600',
    icon: 'text-purple-600'
  }
};

const subjectIcons = {
  Deutsch: '📚',
  Mathematik: '🔢',
  Englisch: '🌍'
};

// Helper function for KI assessment colors
const getKIAssessmentColor = (assessment: KIAssessment) => {
  if (assessment.memo.includes('Roter Alarm')) {
    return 'bg-red-50 border-red-200';
  } else if (assessment.memo.includes('Grüner Alarm')) {
    return 'bg-green-50 border-green-200';
  } else {
    return 'bg-yellow-50 border-yellow-200';
  }
};

export default function StudentInfoModal({ isOpen, onClose, student }: StudentInfoModalProps) {
  const [activeTab, setActiveTab] = useState<'progress' | 'performance' | 'assessment'>('progress');
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [memoText, setMemoText] = useState('');
  const [studentPhoto, setStudentPhoto] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const studentIdentifier = student.sophomorixUnid ?? student.Unid;

  // Foto laden wenn Modal geöffnet wird
  useEffect(() => {
    if (isOpen && studentIdentifier) {
      loadStudentPhoto(studentIdentifier).then(photo => {
        setStudentPhoto(photo);
      });
    }
  }, [isOpen, studentIdentifier]);

  // Query für fächerübergreifende Fortschritts-Daten
  const { data: progressData, isLoading: progressLoading } = useQuery<StudentProgressData>(
    ['student-progress', studentIdentifier],
    async () => {
      const response = await api.get(`/lernbuero/student-progress/${studentIdentifier}`);
      return response.data.data;
    },
    {
      enabled: isOpen && !!studentIdentifier,
      retry: 3,
    }
  );

  // Query für Leistungsdaten
  const { data: performanceData, isLoading: performanceLoading } = useQuery<PerformanceRecord[]>(
    ['student-performance', studentIdentifier],
    async () => {
      const response = await api.get(`/students/${studentIdentifier}/performance`);
      return response.data.data;
    },
    {
      enabled: isOpen && !!studentIdentifier,
      retry: 3,
    }
  );

  // Query für KI-Assessment
  const { data: kiAssessment, isLoading: kiAssessmentLoading } = useQuery<KIAssessment>(
    ['ki-assessment', studentIdentifier],
    async () => {
      const response = await api.get(`/ki/assessment/${studentIdentifier}`);
      return response.data.data;
    },
    {
      enabled: isOpen && !!studentIdentifier,
      retry: false,
      onError: (error: any) => {
        // Silently handle 404 errors for missing assessments
        if (error?.response?.status !== 404) {
          console.error('KI assessment error:', error);
        }
      }
    }
  );

  // Query für Student-Daten (für Memo und Fächer-Einstufungen)
  const { data: studentData, isLoading: studentDataLoading } = useQuery<StudentData>(
    ['student-data', studentIdentifier],
    async () => {
      const response = await api.get(`/students/unid/${studentIdentifier}`);
      return response.data;
    },
    {
      enabled: isOpen && !!studentIdentifier,
      retry: 3,
    }
  );

  // Mutation für das Speichern des Memos
  const updateStudentMemoMutation = useMutation(
    async (memo: string) => {
      const response = await api.put(`/students/memo/${studentIdentifier}`, { memo: memo });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-data', studentIdentifier]);
        setIsEditingMemo(false);
        toast.success('Notizen erfolgreich gespeichert');
      },
      onError: (error: any) => {
        console.error('Error updating memo:', error);
        
        // Handle permission errors specifically
        if (error?.response?.status === 403) {
          const message = error?.response?.data?.message || 'Keine Berechtigung zum Bearbeiten der Notizen für diesen Schüler'
          toast.error(
            <div>
              <div className="font-semibold">Zugriff verweigert</div>
              <div className="text-sm mt-1">{message}</div>
            </div>,
            { duration: 6000 }
          )
        } else if (error?.response?.status === 401) {
          toast.error('Bitte melden Sie sich erneut an')
        } else {
          const message = error?.response?.data?.message || 'Fehler beim Speichern der Notizen'
          toast.error(message)
        }
      },
    }
  );

  // Handler für das Speichern des Memos
  const handleSaveMemo = () => {
    updateStudentMemoMutation.mutate(memoText);
  };

  // Handler für das Abbrechen des Edit-Modus
  const handleCancelMemo = () => {
    setIsEditingMemo(false);
    setMemoText(studentData?.Memo || '');
  };

  // Memo-Text initialisieren wenn Student-Daten geladen werden
  React.useEffect(() => {
    if (studentData?.Memo !== undefined) {
      setMemoText(studentData.Memo);
    }
  }, [studentData?.Memo]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            {hasPhoto(studentPhoto) ? (
              <img
                src={createPhotoDataUrl(studentPhoto!)}
                alt={`Foto von ${student.Name} ${student.Vorname}`}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {student.Vorname} {student.Name}
              </h2>
              <p className="text-gray-600">Klasse: {student.Klasse}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('progress')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'progress'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Fortschritt
            </div>
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'performance'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Leistungsdaten
            </div>
          </button>
          <button
            onClick={() => setActiveTab('assessment')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'assessment'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" />
              Einschätzung
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'progress' ? (
            /* Fortschritts-Tab */
            <div className="space-y-6">
              {progressLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : progressData ? (
                <>
                  {/* Gesamtfortschritt */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <Award className="w-5 h-5 text-blue-600 mr-2" />
                        <h3 className="text-lg font-semibold text-blue-900">Gesamtfortschritt</h3>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-900">
                          {(progressData.overallProgress.baseProgress ?? progressData.overallProgress.progressPercentage ?? 0) + (progressData.overallProgress.bonusProgress ?? 0)}%
                        </div>
                        <div className="text-xs text-blue-600">
                          Basis {progressData.overallProgress.baseProgress ?? progressData.overallProgress.progressPercentage ?? 0}%{progressData.overallProgress.bonusProgress && progressData.overallProgress.bonusProgress > 0 ? ` • Bonus +${progressData.overallProgress.bonusProgress}%` : ''}
                        </div>
                        <div className="text-sm text-blue-700">
                          {progressData.overallProgress.regularTasks !== undefined && progressData.overallProgress.completedRegularTasks !== undefined
                            ? `${progressData.overallProgress.completedRegularTasks} von ${progressData.overallProgress.regularTasks} regulären Aufgaben`
                            : `${progressData.overallProgress.completedTasks} von ${progressData.overallProgress.totalTasks} Aufgaben`
                          }
                          {progressData.overallProgress.bonusTasks && progressData.overallProgress.bonusTasks > 0 && (
                            <span className="block text-amber-700 text-xs font-semibold">
                              +{progressData.overallProgress.completedBonusTasks}/{progressData.overallProgress.bonusTasks} Zusatzaufgaben
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="w-full bg-blue-200 rounded-full h-3">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-in-out"
                        style={{ width: `${Math.min(progressData.overallProgress.baseProgress ?? progressData.overallProgress.progressPercentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Fächer-Fortschritt */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(progressData.subjectProgress).map(([subject, data]) => {
                      const colors = subjectColors[subject as keyof typeof subjectColors];
                      const icon = subjectIcons[subject as keyof typeof subjectIcons];
                      
                      const base = data.baseProgress ?? data.progressPercentage ?? 0
                      const bonus = data.bonusProgress ?? 0
                      const total = base + bonus

                      return (
                        <div 
                          key={subject}
                          className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <span className="text-2xl mr-2">{icon}</span>
                              <h4 className={`font-semibold ${colors.text}`}>{subject}</h4>
                            </div>
                            <div className="text-right">
                              {data.error ? (
                                <div className={`text-xl font-bold ${colors.text}`}>?</div>
                              ) : (
                                <>
                                  <div className={`text-xl font-bold ${colors.text}`}>{total}%</div>
                                  <div className={`text-xs ${colors.text} opacity-80`}>
                                    Basis {base}%{bonus > 0 ? ` • Bonus +${bonus}%` : ''}
                                  </div>
                                  <div className={`text-xs ${colors.text} opacity-75`}>
                                    {data.regularTasks !== undefined && data.completedRegularTasks !== undefined
                                      ? `${data.completedRegularTasks}/${data.regularTasks} regulär`
                                      : `${data.completedTasks}/${data.totalTasks}`
                                    }
                                    {data.bonusTasks && data.bonusTasks > 0 && (
                                      <span className="ml-1 text-amber-700">
                                        +{data.completedBonusTasks}/{data.bonusTasks} Zusatz
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {data.error ? (
                            <div className={`text-sm ${colors.text} opacity-75`}>
                              {data.error}
                            </div>
                          ) : (
                            <>
                              <div className="w-full bg-white bg-opacity-50 rounded-full h-2 mb-2">
                                <div 
                                  className={`${colors.progress} h-2 rounded-full transition-all duration-300 ease-in-out`}
                                style={{ width: `${Math.min(data.baseProgress ?? data.progressPercentage, 100)}%` }}
                                ></div>
                              </div>

                            {(data.learningPlans?.length ?? data.learningPlansCount ?? 0) > 0 && (
                                <div className={`text-xs ${colors.text} opacity-75`}>
                                {(data.learningPlans?.length ?? data.learningPlansCount ?? 0)} Lernplan{(data.learningPlans?.length ?? data.learningPlansCount ?? 0) !== 1 ? 'e' : ''} aktiv
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Keine Fortschrittsdaten verfügbar</p>
                </div>
              )}
            </div>
          ) : activeTab === 'performance' ? (
            /* Leistungsdaten-Tab */
            <div className="space-y-4">
              {performanceLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : performanceData && performanceData.length > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Leistungsdaten (Belege)</h3>
                    <span className="text-sm text-gray-500">{performanceData.length} Einträge gefunden</span>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-semibold text-gray-900">Fach</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-900">Baustein</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-900">Lernplan</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-900">Aufgabe</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-900">Lehrer</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-900">Datum</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {performanceData.slice(0, 20).map((record, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="py-2 px-3 text-gray-900">{record.Fach}</td>
                            <td className="py-2 px-3 text-gray-900">{record.Baustein}</td>
                            <td className="py-2 px-3 text-gray-900">{record.Lernplan}</td>
                            <td className="py-2 px-3 text-gray-900">{record.Aufgabe}</td>
                            <td className="py-2 px-3 text-gray-900">{record.Lehrer}</td>
                            <td className="py-2 px-3 text-gray-900">{record.Datum}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {performanceData.length > 20 && (
                      <div className="text-center py-4 text-gray-500">
                        <p>... und {performanceData.length - 20} weitere Einträge</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Keine Leistungsdaten verfügbar</p>
                </div>
              )}
            </div>
          ) : activeTab === 'assessment' ? (
            /* Assessment-Tab */
            <div className="space-y-6">
              {studentDataLoading || kiAssessmentLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  {/* Fächer-Einstufungen */}
                  {studentData && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
                      <h4 className="text-base font-semibold text-gray-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                        Aktuelle Fächer-Einstufungen
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded-lg border border-green-200">
                          <label className="block text-xs font-medium text-gray-700 mb-2 text-center">Deutsch</label>
                          <div className="text-center">
                            <span className="text-lg font-bold text-green-600">{studentData.Diff_D || '-'}</span>
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-green-200">
                          <label className="block text-xs font-medium text-gray-700 mb-2 text-center">Mathematik</label>
                          <div className="text-center">
                            <span className="text-lg font-bold text-green-600">{studentData.Diff_M || '-'}</span>
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-green-200">
                          <label className="block text-xs font-medium text-gray-700 mb-2 text-center">Englisch</label>
                          <div className="text-center">
                            <span className="text-lg font-bold text-green-600">{studentData.Diff_E || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* KI-Einschätzung */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100 overflow-hidden">
                    <div className="p-3 border-b border-green-200 bg-gradient-to-r from-green-500 to-emerald-500">
                      <h3 className="text-base font-semibold text-white flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Einschätzung der Lernrakete
                      </h3>
                    </div>
                    <div className="p-3">
                      {kiAssessment ? (
                        <div className={`p-3 rounded-lg border ${getKIAssessmentColor(kiAssessment)}`}>
                          <div className="flex items-start space-x-2">
                            {kiAssessment.memo.includes('Roter Alarm') ? (
                              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            ) : kiAssessment.memo.includes('Grüner Alarm') ? (
                              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                            ) : (
                              <div className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            )}
                            <div className="flex-1">
                              <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap">{kiAssessment.memo}</p>
                              <p className="text-xs mt-2 text-gray-600 bg-white px-2 py-1 rounded-full inline-block">
                                {new Date(kiAssessment.datum).toLocaleDateString('de-DE')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            <AlertTriangle className="h-6 w-6 text-gray-400" />
                          </div>
                          <p className="text-xs text-gray-500">Keine KI-Einschätzung verfügbar</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Memo/Notizen */}
                  {studentData && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100 overflow-hidden">
                      <div className="p-3 border-b border-purple-200 bg-gradient-to-r from-purple-500 to-pink-500">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base font-semibold text-white flex items-center">
                            <Edit className="h-4 w-4 mr-2" />
                            Notizen aus dem Lernbüro
                          </h3>
                          {!isEditingMemo && (
                            <button
                              onClick={() => setIsEditingMemo(true)}
                              className="text-white hover:text-purple-200 transition-colors"
                              title="Notizen bearbeiten"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="p-3">
                        {isEditingMemo ? (
                          <div className="space-y-3">
                            <textarea
                              value={memoText}
                              onChange={(e) => setMemoText(e.target.value)}
                              className="w-full p-3 border border-purple-200 rounded-lg text-xs text-gray-900 leading-relaxed resize-none"
                              rows={4}
                              placeholder="Notizen eingeben..."
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={handleSaveMemo}
                                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                                disabled={updateStudentMemoMutation.isLoading}
                              >
                                {updateStudentMemoMutation.isLoading ? (
                                  <>
                                    <RefreshCw className="h-3 w-3 animate-spin" />
                                    Speichere...
                                  </>
                                ) : (
                                  <>
                                    <Save className="h-3 w-3" />
                                    Speichern
                                  </>
                                )}
                              </button>
                              <button
                                onClick={handleCancelMemo}
                                className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded text-xs font-medium hover:bg-gray-50 transition-colors"
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        ) : studentData.Memo ? (
                          <div className="bg-white p-3 rounded-lg border border-purple-200">
                            <p className="text-xs font-medium leading-relaxed whitespace-pre-wrap text-gray-900">
                              {studentData.Memo}
                            </p>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                              <Edit className="h-6 w-6 text-gray-400" />
                            </div>
                            <p className="text-xs text-gray-500">Keine Notizen vorhanden</p>
                            <button
                              onClick={() => setIsEditingMemo(true)}
                              className="mt-2 text-xs text-purple-600 hover:text-purple-800 transition-colors"
                            >
                              Notizen hinzufügen
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
